/* © NXAPP Limited */
/**
 * Media — Letters: letters to the editor.
 *
 * Sibling to the rapid-response pitch desk. Shares the same news feed and
 * sending profile, but produces a proper letter for publication addressed to a
 * paper's letters desk, with the verification block (name/postal address/phone)
 * those desks require. Tabs: News → Approvals → Log.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import { authedFetch } from '@/lib/authedFetch'
import { describeError } from '@/lib/describeError'
import Spinner from '@/components/ui/Spinner'
import EmptyState from '@/components/ui/EmptyState'
import {
  PenLine, Newspaper, SendHorizonal, Radio, Send, Sparkles, X, Check, Mail,
  ExternalLink, AlertTriangle, Award, Reply, Lightbulb,
} from 'lucide-react'
import { cn } from '@/lib/utils'

type Tab = 'digest' | 'approvals' | 'log'
interface DigestIdea { title: string; angle: string; why: string; paper: string; storyId: string; headline: string; outlet: string | null }

// Find the desk whose paper published this story (so a letter answers their own
// article). Matches on the paper's distinctive word (guardian, telegraph…).
function deskForOutlet(outlet: string | null, desks: { outlet: string; email: string }[]) {
  if (!outlet) return null
  const o = outlet.toLowerCase()
  return desks.find(d => { const key = d.outlet.toLowerCase().replace(/^the\s+/, '').split(/[\s(]/)[0]; return key.length >= 3 && o.includes(key) }) ?? null
}

interface Letter { id: string; story_id: string | null; outlet: string; to_email: string; subject: string; body: string; status: string; format: string; replied: boolean; published: boolean; bounced: boolean; created_at: string; sent_at: string | null; media_stories: { headline: string; url: string; published_at: string | null } | null }
interface Profile { id: string; name: string; from_name: string; from_email: string; brand_id: string | null; config_enc: string | null }
interface Settings { sending_profile_id: string | null; sig_name: string | null; sig_phone: string | null; sig_website: string | null; postal_address: string | null; letter_location: string | null; copy_email: string | null }

// UK letters-to-the-editor desks. Verify addresses periodically — they change.
const LETTERS_DESKS = [
  { outlet: 'The Guardian', email: 'guardian.letters@theguardian.com' },
  { outlet: 'The Times', email: 'letters@thetimes.co.uk' },
  { outlet: 'The Telegraph', email: 'dtletters@telegraph.co.uk' },
  { outlet: 'Financial Times', email: 'letters.editor@ft.com' },
  { outlet: 'The Observer', email: 'letters@observer.co.uk' },
  { outlet: 'i (inews)', email: 'letters@inews.co.uk' },
]
// Comment/opinion (op-ed) submission desks. Fewer papers publish a generic one;
// verify before first use.
const COMMENT_DESKS = [
  { outlet: 'The i Paper', email: 'commentdesk@inews.co.uk' },
  { outlet: 'The Independent (Voices)', email: 'voices@independent.co.uk' },
  { outlet: 'The Guardian (Opinion)', email: 'commentisfree@theguardian.com' },
]

function stamp(iso: string | null): string {
  if (!iso) return '—'
  return new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso))
}
function esc(s: string) { return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;') }

// Client mirror of functions/lib/letter-email.ts so the Preview matches the send.
function composeLetterPreview(body: string, s: Settings, story: { headline?: string | null; url?: string | null; date?: string | null }, fromEmail: string): string {
  body = body ?? ''
  const name = s?.sig_name?.trim() || 'Asif Sarangi'
  const href = (u: string) => /^https?:\/\//i.test(u) ? u : `https://${u}`
  const paras = body.trim().split(/\n{2,}/).map(p => `<p style="margin:0 0 14px">${esc(p).replace(/\n/g, '<br>')}</p>`).join('')
  const ref = story?.headline ? `<p style="margin:0 0 14px;color:#555">Re: ${esc(story.headline)}${story.date ? `, ${esc(story.date)}` : ''}${story.url ? ` (<a href="${esc(story.url)}" style="color:inherit">link</a>)` : ''}</p>` : ''
  const signOff = `<p style="margin:18px 0 0">${esc(name)}</p>`
  const v: string[] = [esc(name)]
  if (s?.postal_address?.trim()) v.push(esc(s.postal_address.trim()).replace(/\n/g, '<br>'))
  if (s?.sig_phone?.trim()) v.push(esc(s.sig_phone.trim()))
  v.push(esc(fromEmail ?? ''))
  const web = s?.sig_website?.trim() || 'https://asifsarangi.com/media.html'
  v.push(`<a href="${esc(href(web))}" style="color:inherit">${esc(href(web).replace(/\.html?$/i, ''))}</a>`)
  const verify = `<p style="margin:22px 0 0;color:#888;font-size:13px;line-height:1.6">— For verification, not for publication —<br>${v.join('<br>')}</p>`
  return `<div style="font-family:-apple-system,Segoe UI,Arial,sans-serif;font-size:15px;line-height:1.5;color:#111;padding:16px;max-width:600px"><p style="margin:0 0 14px">Dear Editor,</p>${ref}${paras}${signOff}${verify}</div>`
}

// Client mirror of composeOpEdHtml.
function composeOpEdPreview(body: string, subject: string, s: Settings, story: { headline?: string | null; date?: string | null }, fromEmail: string): string {
  body = body ?? ''; subject = subject ?? ''
  const name = s?.sig_name?.trim() || 'Asif Sarangi'
  const href = (u: string) => /^https?:\/\//i.test(u) ? u : `https://${u}`
  const words = body.trim().split(/\s+/).filter(Boolean).length
  const refText = story?.headline ? `, responding to “${esc(story.headline)}”${story.date ? ` (${esc(story.date)})` : ''}` : ''
  const cover = `<p style="margin:0 0 14px">I'd like to offer the comment piece below — “${esc(subject)}”, around ${words} words${refText}. Happy to tailor the length or angle to fit.</p>`
  const paras = body.trim().split(/\n{2,}/).map(p => `<p style="margin:0 0 14px">${esc(p).replace(/\n/g, '<br>')}</p>`).join('')
  const contact: string[] = []
  if (s?.sig_phone?.trim()) contact.push(esc(s.sig_phone.trim()))
  contact.push(esc(fromEmail ?? ''))
  const web = s?.sig_website?.trim() || 'https://asifsarangi.com/media.html'
  contact.push(`<a href="${esc(href(web))}" style="color:inherit">${esc(href(web).replace(/\.html?$/i, ''))}</a>`)
  const bio = `<p style="margin:22px 0 0;color:#555;font-size:14px;line-height:1.6">${esc(name)} is a Fractional CTO and AI & cybersecurity adviser who advises founders, family offices and UK businesses on AI adoption, cybersecurity and GDPR.<br>${contact.join(' · ')}</p>`
  return `<div style="font-family:-apple-system,Segoe UI,Arial,sans-serif;font-size:15px;line-height:1.5;color:#111;padding:16px;max-width:640px"><p style="margin:0 0 14px">Dear Comment editor,</p>${cover}<hr style="border:none;border-top:1px solid #eee;margin:18px 0">${paras}${bio}</div>`
}

export default function LettersPage() {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const [tab, setTab] = useState<Tab>('digest')
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [banner, setBanner] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null)
  const note = (kind: 'ok' | 'err', text: string) => { setBanner({ kind, text }); setTimeout(() => setBanner(null), 6000) }

  const lettersQ = useQuery({ queryKey: ['media_letters', user?.id], enabled: !!user, queryFn: async () => {
    const { data, error } = await supabase.from('media_letters').select('*, media_stories(headline,url,published_at)').eq('user_id', user!.id).order('created_at', { ascending: false }).limit(200)
    if (error) throw error; return data as Letter[]
  } })
  const profilesQ = useQuery({ queryKey: ['sending_profiles', user?.id], enabled: !!user, queryFn: async () => {
    const { data, error } = await supabase.from('sending_profiles').select('id, name, from_name, from_email, brand_id, config_enc').eq('user_id', user!.id).order('name')
    if (error) throw error; return data as Profile[]
  } })
  const settingsQ = useQuery({ queryKey: ['media_settings', user?.id], enabled: !!user, queryFn: async () => {
    const { data } = await supabase.from('media_settings').select('sending_profile_id, sig_name, sig_phone, sig_website, postal_address, letter_location, copy_email').eq('user_id', user!.id).maybeSingle()
    return (data as Settings | null) ?? { sending_profile_id: null, sig_name: null, sig_phone: null, sig_website: null, postal_address: null, letter_location: null, copy_email: null }
  } })

  const invalidate = (...keys: string[]) => keys.forEach(k => qc.invalidateQueries({ queryKey: [k, user?.id] }))
  const chooseProfile = useMutation({
    mutationFn: async (id: string | null) => { const { error } = await supabase.from('media_settings').upsert({ user_id: user!.id, sending_profile_id: id, updated_at: new Date().toISOString() }, { onConflict: 'user_id' }); if (error) throw error },
    onSuccess: () => { invalidate('media_settings'); note('ok', 'Sending profile updated.') }, onError: e => note('err', describeError(e)),
  })
  const saveDetails = useMutation({
    mutationFn: async (v: Partial<Settings>) => { const { error } = await supabase.from('media_settings').upsert({ user_id: user!.id, ...v, updated_at: new Date().toISOString() }, { onConflict: 'user_id' }); if (error) throw error },
    onSuccess: () => { invalidate('media_settings'); note('ok', 'Letter details saved.'); setDetailsOpen(false) }, onError: e => note('err', describeError(e)),
  })

  const fromEmail = (() => {
    const profs = profilesQ.data ?? []
    const chosen = profs.find(p => p.id === (settingsQ.data?.sending_profile_id ?? ''))
    return chosen?.from_email || profs.find(p => p.from_email.toLowerCase() === 'press@asifsarangi.com')?.from_email || 'press@asifsarangi.com'
  })()

  const pending = (lettersQ.data ?? []).filter(l => l.status === 'pending')
  const addressSet = !!settingsQ.data?.postal_address?.trim()

  const tabs: { key: Tab; label: string; icon: typeof PenLine; badge?: number }[] = [
    { key: 'digest', label: 'This month', icon: Lightbulb },
    { key: 'approvals', label: 'Approvals', icon: SendHorizonal, badge: pending.length },
    { key: 'log', label: 'Log', icon: Radio },
  ]

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2"><PenLine className="w-6 h-6 text-brand-600" /> Media — Letters</h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">Turn a story into a proper letter to the editor — drafted, edited and approved by you, with the name/address/phone letters desks require. Same news feed and sending profile as the pitch desk.</p>
        </div>
        <button className="btn-secondary" onClick={() => setDetailsOpen(true)}><Mail className="w-4 h-4" /> Letter details</button>
      </div>

      {banner && <div className={cn('rounded-lg border px-4 py-3 text-sm', banner.kind === 'ok' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800')}>{banner.text}</div>}

      {!addressSet && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0" /> Letters desks require your full name, postal address and phone for verification. <button className="underline font-medium" onClick={() => setDetailsOpen(true)}>Add them</button> before sending.
        </div>
      )}

      {/* Sending profile */}
      <div className="card px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        <span className="flex items-center gap-2 text-gray-500"><Send className="w-4 h-4" /> Letters send from</span>
        <select className="input max-w-xs py-1.5" value={settingsQ.data?.sending_profile_id ?? ''} onChange={e => chooseProfile.mutate(e.target.value || null)} disabled={chooseProfile.isPending}>
          <option value="">Press default ({fromEmail})</option>
          {(profilesQ.data ?? []).map(p => <option key={p.id} value={p.id}>{p.from_name} &lt;{p.from_email}&gt;</option>)}
        </select>
        {settingsQ.data?.copy_email && <span className="badge bg-gray-100 text-gray-600">BCC → {settingsQ.data.copy_email}</span>}
      </div>

      <div className="flex gap-1 border-b border-gray-200 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={cn('flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap', tab === t.key ? 'border-brand-600 text-brand-700' : 'border-transparent text-gray-500 hover:text-gray-800')}>
            <t.icon className="w-4 h-4" /> {t.label}{!!t.badge && <span className="badge bg-brand-100 text-brand-700">{t.badge}</span>}
          </button>
        ))}
      </div>

      {tab === 'digest' && <DigestTab invalidate={invalidate} note={note} onDrafted={() => setTab('approvals')} />}
      {tab === 'approvals' && <ApprovalsTab letters={pending} loading={lettersQ.isLoading} userId={user!.id} settings={settingsQ.data ?? null} fromEmail={fromEmail} invalidate={invalidate} note={note} />}
      {tab === 'log' && <LogTab letters={(lettersQ.data ?? []).filter(l => l.status !== 'pending')} loading={lettersQ.isLoading} userId={user!.id} invalidate={invalidate} note={note} />}

      {detailsOpen && <DetailsModal initial={settingsQ.data ?? null} fromEmail={fromEmail} saving={saveDetails.isPending} onCancel={() => setDetailsOpen(false)} onSave={v => saveDetails.mutate(v)} />}
    </div>
  )
}

type Note = (k: 'ok' | 'err', t: string) => void
type Invalidate = (...keys: string[]) => void

// ── This month: synthesise the month's news → letter-worthy ideas → draft ─────
function DigestTab({ invalidate, note, onDrafted }: { invalidate: Invalidate; note: Note; onDrafted: () => void }) {
  const [data, setData] = useState<{ overview: string; ideas: DigestIdea[]; count: number } | null>(null)
  const analyse = useMutation({
    mutationFn: async () => {
      const r = await authedFetch('/api/letters/digest', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ days: 30 }) })
      if (!r.ok) throw new Error(await r.text())
      return r.json() as Promise<{ overview: string; ideas: DigestIdea[]; count: number }>
    },
    onSuccess: d => { setData(d); if (!d.count) note('err', 'No news from the last month — ingest stories in the pitch desk first.') },
    onError: e => note('err', describeError(e)),
  })

  return (
    <div className="space-y-4">
      <div className="card p-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Lightbulb className="w-5 h-5 text-brand-600" /> This month, in letters</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">Reads the last 30 days of your news feed and proposes the letters actually worth writing — the threads where you have a real, non-obvious point. Pick one and it drafts a full letter for your approval.</p>
        </div>
        <button className="btn-primary" onClick={() => analyse.mutate()} disabled={analyse.isPending}>
          {analyse.isPending ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Sparkles className="w-4 h-4" />} {data ? 'Re-analyse' : "Analyse this month"}
        </button>
      </div>

      {!data ? (
        <EmptyState icon={Lightbulb} title="Nothing analysed yet" description="Hit “Analyse this month” to synthesise the month's news into a short read and a few letter ideas worth pursuing." />
      ) : data.count === 0 ? (
        <EmptyState icon={Newspaper} title="No news in the last month" description="The feed is shared with the pitch desk — ingest there first, then come back." />
      ) : (
        <>
          {data.overview && (
            <div className="card p-5">
              <p className="text-xs font-medium text-gray-400 mb-1">The month in brief · {data.count} stories</p>
              <p className="text-[15px] text-gray-800 leading-relaxed">{data.overview}</p>
            </div>
          )}
          <p className="text-sm font-medium text-gray-700">Letters worth writing</p>
          {data.ideas.map((idea, i) => <IdeaCard key={i} idea={idea} invalidate={invalidate} note={note} onDrafted={onDrafted} />)}
        </>
      )}
    </div>
  )
}

function IdeaCard({ idea, invalidate, note, onDrafted }: { idea: DigestIdea; invalidate: Invalidate; note: Note; onDrafted: () => void }) {
  const [format, setFormat] = useState<'letter' | 'oped'>('letter')
  const desks = format === 'oped' ? COMMENT_DESKS : LETTERS_DESKS
  // A letter must answer the paper's OWN article — default to the story's outlet.
  const ownDesk = deskForOutlet(idea.outlet, LETTERS_DESKS)
  const [outlet, setOutlet] = useState(ownDesk?.outlet ?? LETTERS_DESKS[0].outlet)
  const desk = desks.find(d => d.outlet === outlet) ?? desks[0]
  const setFmt = (fmt: 'letter' | 'oped') => { setFormat(fmt); setOutlet((fmt === 'oped' ? (deskForOutlet(idea.outlet, COMMENT_DESKS) ?? COMMENT_DESKS[0]) : (ownDesk ?? LETTERS_DESKS[0])).outlet) }
  const mismatch = format === 'letter' && !!idea.outlet && !deskForOutlet(idea.outlet, LETTERS_DESKS)
  const gen = useMutation({
    mutationFn: async () => {
      const r = await authedFetch('/api/letters/draft', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ storyId: idea.storyId, outlet: desk.outlet, toEmail: desk.email, angle: idea.angle, format }) })
      if (!r.ok) throw new Error(await r.text())
      return r.json()
    },
    onSuccess: () => { invalidate('media_letters'); note('ok', `${format === 'oped' ? 'Comment piece' : 'Letter'} for ${desk.outlet} drafted — in Approvals for your edit.`); onDrafted() },
    onError: e => note('err', describeError(e)),
  })
  return (
    <div className="card p-5 space-y-3">
      <div>
        <h3 className="font-semibold text-gray-900">{idea.title}</h3>
        <p className="text-[15px] text-gray-800 mt-1">{idea.angle}</p>
        <p className="text-sm text-gray-500 mt-1"><span className="text-gray-400">Why now:</span> {idea.why}</p>
        <p className="text-xs text-gray-400 mt-1">Anchored to: {idea.headline}{idea.outlet ? ` · ${idea.outlet}` : ''}</p>
      </div>
      {mismatch && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>This story is from <strong>{idea.outlet}</strong>, which isn't one of the letters-desk papers. A letter answers a paper's <em>own</em> article — so it'll be written to address the issue, not "your report". For a story from another outlet, a <strong>comment piece</strong> is usually the better fit.</span>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-sm">
          <button onClick={() => setFmt('letter')} className={cn('px-3 py-1.5', format === 'letter' ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-50')}>Letter</button>
          <button onClick={() => setFmt('oped')} className={cn('px-3 py-1.5 border-l border-gray-200', format === 'oped' ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-50')}>Comment piece</button>
        </div>
        <select className="input max-w-xs py-1.5 text-sm" value={outlet} onChange={e => setOutlet(e.target.value)}>
          {desks.map(d => <option key={d.outlet} value={d.outlet}>{d.outlet}</option>)}
        </select>
        <button className="btn-primary" onClick={() => gen.mutate()} disabled={gen.isPending}>
          {gen.isPending ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <PenLine className="w-4 h-4" />} Draft {format === 'oped' ? 'comment piece' : 'this letter'}
        </button>
      </div>
      {format === 'oped' && <p className="text-xs text-gray-400">Comment pieces (~800 words) go to a paper's opinion desk — fewer papers, and verify the address before first use.</p>}
    </div>
  )
}

// ── Approvals ─────────────────────────────────────────────────────────────────
function ApprovalsTab({ letters, loading, userId, settings, fromEmail, invalidate, note }: { letters: Letter[]; loading: boolean; userId: string; settings: Settings | null; fromEmail: string; invalidate: Invalidate; note: Note }) {
  if (loading) return <div className="py-16 text-center"><Spinner className="w-6 h-6 mx-auto" /></div>
  if (!letters.length) return <EmptyState icon={SendHorizonal} title="No letters waiting" description="Draft one from the This-month tab and it lands here to edit and approve." />
  return <div className="space-y-4">{letters.map(l => <LetterCard key={l.id} letter={l} userId={userId} settings={settings} fromEmail={fromEmail} invalidate={invalidate} note={note} />)}</div>
}

const DEFAULT_SETTINGS: Settings = { sending_profile_id: null, sig_name: null, sig_phone: null, sig_website: null, postal_address: null, letter_location: null, copy_email: null }

function LetterCard({ letter, userId, settings, fromEmail, invalidate, note }: { letter: Letter; userId: string; settings: Settings | null; fromEmail: string; invalidate: Invalidate; note: Note }) {
  const [subject, setSubject] = useState(letter.subject)
  const [body, setBody] = useState(letter.body)
  const [view, setView] = useState<'edit' | 'preview'>('edit')
  const [sent, setSent] = useState(false)
  const edited = subject !== letter.subject || body !== letter.body
  const story = letter.media_stories
  const words = (body ?? '').trim().split(/\s+/).filter(Boolean).length
  const isOpEd = letter.format === 'oped'
  const overLimit = isOpEd ? (words < 600 || words > 900) : words > 300

  const send = useMutation({
    mutationFn: async () => {
      const r = await authedFetch('/api/letters/send', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ letterId: letter.id, subject, body }) })
      if (!r.ok) throw new Error(await r.text())
      return r.json()
    },
    onSuccess: () => { setSent(true); invalidate('media_letters'); note('ok', `${isOpEd ? 'Comment piece' : 'Letter'} sent to ${letter.outlet}. It's in the Log now.`) },
    onError: e => note('err', describeError(e)),
  })
  const skip = useMutation({
    mutationFn: async () => { const { error } = await supabase.from('media_letters').update({ status: 'skipped' }).eq('id', letter.id).eq('user_id', userId); if (error) throw error },
    onSuccess: () => { invalidate('media_letters'); note('ok', 'Skipped.') }, onError: e => note('err', describeError(e)),
  })

  const date = story?.published_at ? new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(story.published_at)) : null

  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1"><span className="badge bg-brand-50 text-brand-700 border border-brand-200">{letter.outlet}</span><span className="badge bg-gray-100 text-gray-600">{isOpEd ? 'comment piece' : 'letter'}</span><span className="text-xs text-gray-500">{letter.to_email}</span></div>
          {story && <a href={story.url} target="_blank" rel="noreferrer" className="text-sm text-gray-600 hover:text-brand-700 inline-flex items-center gap-1">Re: {story.headline} <ExternalLink className="w-3.5 h-3.5 shrink-0" /></a>}
        </div>
      </div>

      <div className="space-y-2">
        <div><label className="label">Subject</label><input className="input" value={subject} onChange={e => setSubject(e.target.value)} /></div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="label mb-0">{isOpEd ? 'Comment piece' : 'Letter'} {edited && <span className="text-brand-600 text-xs">· edited</span>}</label>
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden text-xs">
              <button onClick={() => setView('edit')} className={cn('px-3 py-1', view === 'edit' ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-50')}>Edit</button>
              <button onClick={() => setView('preview')} className={cn('px-3 py-1 border-l border-gray-200', view === 'preview' ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-500 hover:bg-gray-50')}>Preview</button>
            </div>
          </div>
          {view === 'edit'
            ? <textarea className={cn('input text-[14px] leading-relaxed', isOpEd ? 'min-h-[360px]' : 'min-h-[220px]')} value={body} onChange={e => setBody(e.target.value)} />
            : <div className={cn('border border-gray-200 rounded-lg overflow-hidden bg-white', isOpEd ? 'h-[32rem]' : 'h-96')}><iframe srcDoc={isOpEd ? composeOpEdPreview(body, subject, settings ?? DEFAULT_SETTINGS, { headline: story?.headline, date }, fromEmail) : composeLetterPreview(body, settings ?? DEFAULT_SETTINGS, { headline: story?.headline, url: story?.url, date }, fromEmail)} className="w-full h-full" sandbox="" title="Preview" /></div>}
          <p className={cn('text-xs mt-1', overLimit ? 'text-amber-600' : 'text-gray-400')}>{words} words · {isOpEd ? 'aim ~700–850' : 'aim under 300'} · {isOpEd ? 'a covering note to the comment editor + your bio are added around it' : '"Dear Editor", the article reference and your verification details are added around it'} — see Preview</p>
        </div>
      </div>

      {sent ? (
        <div className="flex items-center gap-2 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800"><Check className="w-4 h-4" /> Sent to {letter.outlet}. It's in the Log now.</div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <button className="btn-primary" onClick={() => send.mutate()} disabled={send.isPending}>{send.isPending ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Send className="w-4 h-4" />}{send.isPending ? 'Sending…' : edited ? 'Edit & Send' : 'Send'}</button>
          <button className="btn-secondary" onClick={() => skip.mutate()} disabled={skip.isPending}>Skip</button>
        </div>
      )}
    </div>
  )
}

// ── Log ───────────────────────────────────────────────────────────────────────
function LogTab({ letters, loading, userId, invalidate, note }: { letters: Letter[]; loading: boolean; userId: string; invalidate: Invalidate; note: Note }) {
  const toggle = useMutation({
    mutationFn: async ({ id, field, value }: { id: string; field: 'replied' | 'published' | 'bounced'; value: boolean }) => { const { error } = await supabase.from('media_letters').update({ [field]: value }).eq('id', id).eq('user_id', userId); if (error) throw error },
    onSuccess: () => invalidate('media_letters'),
  })
  const markUndelivered = useMutation({
    mutationFn: async (id: string) => { const { error } = await supabase.from('media_letters').update({ bounced: true }).eq('id', id).eq('user_id', userId); if (error) throw error },
    onSuccess: () => { invalidate('media_letters'); note('ok', 'Flagged undelivered — that desk address bounced. Correct it, or re-draft from “This month” to a different paper.') },
    onError: e => note('err', describeError(e)),
  })
  if (loading) return <div className="py-16 text-center"><Spinner className="w-6 h-6 mx-auto" /></div>
  if (!letters.length) return <EmptyState icon={Radio} title="No letters sent yet" description="Approved letters are logged here, with published / reply tracking." />
  return (
    <div className="card divide-y divide-gray-100">
      {letters.map(l => (
        <div key={l.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-900 truncate">{l.subject}</div>
            <div className="text-gray-500">{l.outlet} · {l.to_email} · {stamp(l.sent_at ?? l.created_at)}</div>
          </div>
          {l.status === 'skipped' && <span className="badge bg-gray-100 text-gray-500">skipped</span>}
          {l.bounced && <span className="badge bg-red-100 text-red-800 border border-red-300">undelivered</span>}
          {l.status === 'sent' && !l.bounced && <>
            <button onClick={() => toggle.mutate({ id: l.id, field: 'replied', value: !l.replied })} className={cn('badge border', l.replied ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-white text-gray-400 border-gray-200')}><Reply className="w-3 h-3 mr-1" /> {l.replied ? 'replied' : 'no reply'}</button>
            <button onClick={() => toggle.mutate({ id: l.id, field: 'published', value: !l.published })} className={cn('badge border', l.published ? 'bg-green-50 text-green-700 border-green-200' : 'bg-white text-gray-400 border-gray-200')}><Award className="w-3 h-3 mr-1" /> {l.published ? 'published' : 'mark published'}</button>
            <button onClick={() => { if (confirm(`Flag ${l.to_email} as undelivered?`)) markUndelivered.mutate(l.id) }} disabled={markUndelivered.isPending} className="badge border bg-white text-red-600 border-red-200 hover:bg-red-50"><X className="w-3 h-3 mr-1" /> undelivered</button>
          </>}
        </div>
      ))}
    </div>
  )
}

// ── Letter details (verification) ─────────────────────────────────────────────
function DetailsModal({ initial, fromEmail, saving, onCancel, onSave }: { initial: Settings | null; fromEmail: string; saving: boolean; onCancel: () => void; onSave: (v: Partial<Settings>) => void }) {
  const [f, setF] = useState({
    sig_name: initial?.sig_name ?? 'Asif Sarangi',
    letter_location: '',
    postal_address: initial?.postal_address ?? '',
    sig_phone: initial?.sig_phone ?? '+44 (0) 7366 247 007',
    sig_website: initial?.sig_website ?? 'https://asifsarangi.com/media.html',
    copy_email: initial?.copy_email ?? '',
  })
  const set = (k: keyof typeof f, v: string) => setF(s => ({ ...s, [k]: v }))
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="card w-full max-w-md p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-semibold text-gray-900">Letter details</h3><button onClick={onCancel} className="p-1 text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button></div>
        <p className="text-sm text-gray-500">Letters desks require these to verify you. Your name signs the letter; the address, phone and email are for verification only, not published.</p>
        <div><label className="label">Name</label><input className="input" value={f.sig_name} onChange={e => set('sig_name', e.target.value)} /></div>
        <div><label className="label">Postal address <span className="text-amber-600 font-normal">— required</span></label><textarea className="input min-h-[70px]" value={f.postal_address} onChange={e => set('postal_address', e.target.value)} placeholder="12 Example Street, London, W1A 1AA" /></div>
        <div><label className="label">Website / media page</label><input className="input" value={f.sig_website} onChange={e => set('sig_website', e.target.value)} placeholder="https://asifsarangi.com/media.html" /><p className="text-xs text-gray-400 mt-1">Shown with your verification details; the visible text hides any .html.</p></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="label">Phone</label><input className="input" value={f.sig_phone} onChange={e => set('sig_phone', e.target.value)} /></div>
          <div><label className="label">BCC me</label><input className="input" value={f.copy_email} onChange={e => set('copy_email', e.target.value)} placeholder="vip@asifsarangi.com" /></div>
        </div>
        <p className="text-xs text-gray-400">Contact email on the letter is your sending address: {fromEmail}</p>
        <div className="flex justify-end gap-2"><button className="btn-secondary" onClick={onCancel}>Cancel</button><button className="btn-primary" onClick={() => onSave(f)} disabled={saving}>{saving ? <Spinner className="w-4 h-4 border-white border-t-transparent" /> : <Check className="w-4 h-4" />} Save</button></div>
      </div>
    </div>
  )
}
